import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { motion } from 'framer-motion';
import { 
  Users, 
  UserCircle, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  TrendingUp,
  ArrowRight,
  ClipboardPlus,
  Play,
  Briefcase
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayJobs, setTodayJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, jobsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/jobs', { params: { date_from: new Date().toISOString().split('T')[0], date_to: new Date().toISOString().split('T')[0] } })
      ]);
      setStats(statsRes.data);
      setTodayJobs(jobsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Clientes', 
      value: stats?.total_clients || 0, 
      icon: UserCircle, 
      color: 'bg-blue-500',
      link: '/clients'
    },
    { 
      title: 'Trabajadores', 
      value: stats?.total_workers || 0, 
      icon: Users, 
      color: 'bg-purple-500',
      link: '/workers'
    },
    { 
      title: 'Trabajos hoy', 
      value: stats?.jobs_today || 0, 
      icon: Calendar, 
      color: 'bg-sky-500',
      link: '/calendar'
    },
    { 
      title: 'Completados este mes', 
      value: stats?.completed_this_month || 0, 
      icon: CheckCircle2, 
      color: 'bg-green-500',
      link: '/reports'
    },
    { 
      title: 'Pendientes', 
      value: stats?.pending_jobs || 0, 
      icon: Clock, 
      color: 'bg-amber-500',
      link: '/calendar'
    },
    { 
      title: 'Urgencias hoy', 
      value: stats?.emergencies_today || 0, 
      icon: AlertTriangle, 
      color: 'bg-red-500',
      link: '/calendar'
    },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      scheduled: 'Programado',
      in_progress: 'En progreso',
      completed: 'Completado',
      cancelled: 'Cancelado'
    };
    return <Badge className={styles[status] || styles.scheduled}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="space-y-8" data-testid="owner-dashboard">
        {/* Quick Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/new-report" className="flex-1">
            <Card className="h-full bg-gradient-to-br from-sky-500 to-blue-600 text-white hover:from-sky-600 hover:to-blue-700 transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <ClipboardPlus className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Crear Parte Rápido</h3>
                  <p className="text-sm text-white/80">Iniciar un parte de trabajo ad-hoc</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/my-jobs" className="flex-1">
            <Card className="h-full bg-gradient-to-br from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <Briefcase className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Mis Trabajos</h3>
                  <p className="text-sm text-white/80">Ver y completar trabajos asignados</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={stat.link}>
                  <Card className="hover:shadow-md transition-shadow cursor-pointer">
                    <CardContent className="p-4">
                      <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-3`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                      <p className="text-sm text-slate-500">{stat.title}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Today's Jobs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-display text-xl">Trabajos de hoy</CardTitle>
            <Link to="/calendar">
              <Button variant="ghost" size="sm">
                Ver calendario
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {todayJobs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No hay trabajos programados para hoy</p>
                <Link to="/calendar">
                  <Button variant="outline" className="mt-4">
                    Programar trabajo
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {todayJobs.slice(0, 5).map((job) => (
                  <div 
                    key={job.id} 
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-slate-900">{job.title}</h4>
                        {job.is_emergency && (
                          <Badge className="bg-red-100 text-red-800 text-xs">URGENTE</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">
                        {job.client_name} • {job.client_address}
                      </p>
                      {job.worker_name && (
                        <p className="text-sm text-slate-400 mt-1">
                          Asignado a: {job.worker_name}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {job.scheduled_time && (
                        <span className="text-sm text-slate-500">{job.scheduled_time}</span>
                      )}
                      {getStatusBadge(job.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/new-report">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full border-sky-200 bg-sky-50">
              <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-sky-500 flex items-center justify-center flex-shrink-0">
                  <ClipboardPlus className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Nuevo parte</h3>
                  <p className="text-xs sm:text-sm text-slate-500 truncate">Crear parte de trabajo</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/clients">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <UserCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Nuevo cliente</h3>
                  <p className="text-xs sm:text-sm text-slate-500 truncate">Añade un nuevo cliente</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/workers">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Invitar trabajador</h3>
                  <p className="text-xs sm:text-sm text-slate-500 truncate">Añade a tu equipo</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/calendar">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-sky-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Programar trabajo</h3>
                  <p className="text-xs sm:text-sm text-slate-500 truncate">Agenda una visita</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </OwnerLayout>
  );
};

export default Dashboard;
